"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LineChart as LineChartIcon,
  Loader2,
  PieChart as PieIcon,
  Save,
  Table2,
} from "lucide-react";
import type {
  ReportingPeriodPayload,
  RevenueDashboardBlock,
  RfoDashboardRoot,
  StringNumberMap,
} from "@/utils/rfo-revenue-dashboard-utils";

function dashboardHasSection(dash: RevenueDashboardBlock | undefined, key: keyof RevenueDashboardBlock): boolean {
  if (!dash) return false;
  const v = dash[key];
  if (v === undefined || v === null) return false;
  if (typeof v === "object" && !Array.isArray(v) && Object.keys(v as Record<string, unknown>).length === 0) {
    return false;
  }
  return true;
}
import {
  buildProductRateMomTableRows,
  buildRankBarRows,
  entriesSortedByValueDesc,
  formatCompact,
  formatNum,
  getPreviousReportingDate,
  sortMonthKeys,
  sortReportingDateKeys,
  top10OverlapSummary,
  topNAndOthers,
} from "@/utils/rfo-revenue-dashboard-utils";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getFY_YEAR } from "@/utils/sharepoint-env";

const EPS = 1e-9;

const USER_RESPONSES_FILE = "Execution_RFO_UserResponses.json";
const USER_RESPONSES_SECTION = "Execution_RFO_UserResponses";
const RATE_MOM_PREFIX = "rate-mom:";
function rateMomRemarkKey(product: string, month: string): string {
  return `${RATE_MOM_PREFIX}${encodeURIComponent(product)}§${encodeURIComponent(month)}`;
}
const QTY_TOTAL_PREFIX = "qty-total:";
const QTY_PRODUCT_PREFIX = "qty-product:";
function qtyTotalRemarkKey(month: string): string {
  return `${QTY_TOTAL_PREFIX}${encodeURIComponent(month)}`;
}
function qtyProductRemarkKey(product: string, month: string): string {
  return `${QTY_PRODUCT_PREFIX}${encodeURIComponent(product)}§${encodeURIComponent(month)}`;
}

/** CategoryWisePane `sectionId` → `dashboard` key in Execution_RFO_UserResponses.json */
const SECTION_TO_DASHBOARD_KEY: Record<string, string> = {
  cust: "cust_anal",
  inco: "intcom_anal",
  exp: "exp_dom_anal",
  prod: "prod_type_anal",
  plant: "plant_loc_anal",
  region: "region_anal",
  tx: "tansaction_type_anal",
};

const PIE_COLORS = [
  "#22d3ee",
  "#a78bfa",
  "#34d399",
  "#fbbf24",
  "#f472b6",
  "#60a5fa",
  "#fb7185",
  "#4ade80",
  "#c084fc",
  "#facc15",
  "#64748b",
];

/**
 * Loads dashboard JSON only from SharePoint: `{FY_YEAR}/juggernaut/Execution_RFO_Dashboard.json`
 * (same path as Graph download in main process). Tenant/site/library/FY_YEAR come from app config / database.
 */
async function loadDashboardJson(): Promise<{ data: RfoDashboardRoot; error?: string }> {
  const downloadClientJson = (
    window as unknown as {
      sharePointAPI?: {
        downloadClientJson?: (fileName: string) => Promise<{ success?: boolean; data?: unknown; error?: string }>;
      };
    }
  ).sharePointAPI?.downloadClientJson;

  if (!downloadClientJson) {
    return {
      data: {},
      error: "SharePoint is not available. Open this screen from the desktop app with an engagement selected.",
    };
  }

  try {
    const sp = await downloadClientJson("Execution_RFO_Dashboard.json");
    if (!sp?.success) {
      return {
        data: {},
        error: sp?.error ?? "Failed to download Execution_RFO_Dashboard.json from SharePoint.",
      };
    }

    const raw = sp.data;
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
      return {
        data: {},
        error: "Invalid JSON received from SharePoint for Execution_RFO_Dashboard.json.",
      };
    }

    const data = raw as RfoDashboardRoot;
    if (Object.keys(data).length === 0) {
      return {
        data: {},
        error:
          "Run the IPE Testing to Load this Section",
      };
    }

    return { data };
  } catch (e) {
    return {
      data: {},
      error: e instanceof Error ? e.message : "Failed to load Execution_RFO_Dashboard.json from SharePoint.",
    };
  }
}

function ScrollSection({
  id,
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className="box-border w-[min(96.8vw,880px)] max-w-[min(96.8vw,880px)] shrink-0 snap-center rounded-2xl border border-white/10 bg-gradient-to-br from-slate-950/90 via-slate-900/80 to-slate-950/90 p-4 shadow-xl shadow-black/40 ring-1 ring-white/5 sm:p-5"
    >
      <div className="mb-4 flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-white">
            {Icon && <Icon className="h-5 w-5 shrink-0 text-cyan-400" />}
            {title}
          </h3>
          {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
        </div>
      </div>
      <div className="min-w-0 max-w-full overflow-x-hidden">{children}</div>
    </section>
  );
}

function PieTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; payload?: { name?: string; value?: number; percent?: number } }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const label = row?.name ?? payload[0].name ?? "";
  const v = Number(row?.value ?? payload[0].value ?? 0);
  const pct = row?.percent != null ? String(Math.round(row.percent * 100)) : "";
  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/95 px-3 py-2 text-xs text-white shadow-lg">
      <div className="font-medium">{label}</div>
      <div className="text-cyan-300">{formatNum(v)}</div>
      {pct !== "" && <div className="text-slate-400">{pct}% of total</div>}
    </div>
  );
}

function CategoryWisePane({
  cy,
  py,
  sectionId,
  remarks,
  onRemark,
  onSaveRemarks,
  isSavingRemarks,
  saveRemarksDisabled,
}: {
  cy: StringNumberMap | undefined;
  py: StringNumberMap | undefined;
  sectionId: string;
  remarks: Record<string, string>;
  onRemark: (key: string, v: string) => void;
  onSaveRemarks?: () => void;
  isSavingRemarks?: boolean;
  saveRemarksDisabled?: boolean;
}) {
  const RANK_PAGE_SIZE = 10;
  const [rankPage, setRankPage] = useState(0);

  const { top, othersSum } = useMemo(() => topNAndOthers(cy, 10), [cy]);
  const total = useMemo(() => Object.values(cy ?? {}).reduce((s, v) => s + v, 0), [cy]);
  const pieData = useMemo(() => {
    const rows = top.map(([name, value]) => ({
      name: name.length > 42 ? `${name.slice(0, 40)}…` : name,
      value,
      fullName: name,
      percent: total !== 0 ? value / total : 0,
    }));
    if (othersSum !== 0) {
      rows.push({
        name: "Others",
        value: othersSum,
        fullName: "Others",
        percent: total !== 0 ? othersSum / total : 0,
      });
    }
    return rows;
  }, [top, othersSum, total]);

  const rankRows = useMemo(() => buildRankBarRows(cy), [cy]);
  const totalRankPages = Math.max(1, Math.ceil(rankRows.length / RANK_PAGE_SIZE) || 1);
  const safeRankPage = Math.min(rankPage, totalRankPages - 1);
  const barPageData = useMemo(() => {
    const start = safeRankPage * RANK_PAGE_SIZE;
    return rankRows.slice(start, start + RANK_PAGE_SIZE).map((r) => ({
      rank: r.rank,
      value: r.value,
      entityName: r.entityName,
      label: String(r.rank),
    }));
  }, [rankRows, safeRankPage]);

  useEffect(() => {
    setRankPage(0);
  }, [sectionId]);

  useEffect(() => {
    setRankPage((p) => Math.min(p, Math.max(0, totalRankPages - 1)));
  }, [totalRankPages, rankRows.length]);

  const overlap = useMemo(() => top10OverlapSummary(cy, py), [cy, py]);

  const tableRows = useMemo(() => entriesSortedByValueDesc(cy).slice(0, 10), [cy]);
  const pyTotal = useMemo(() => Object.values(py ?? {}).reduce((s, v) => s + v, 0), [py]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-100/90">
        <span className="font-medium text-amber-300">Top 10 composition vs prior period: </span>
        {overlap.overlap}/10 names overlap.
        {overlap.cyOnly.length > 0 && (
          <span className="ml-2 block sm:inline">
            New in current top 10:{" "}
            <span className="text-cyan-300">{overlap.cyOnly.slice(0, 3).join(", ")}</span>
            {overlap.cyOnly.length > 3 ? "…" : ""}
          </span>
        )}
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
        <div className="min-w-0 overflow-hidden rounded-xl border border-white/5 bg-black/30 p-3 sm:p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-300">
            <PieIcon className="h-4 w-4 shrink-0 text-cyan-400" /> Share (top 10 + others)
          </div>
          <div className="h-[260px] w-full max-w-full sm:h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={52}
                  outerRadius={88}
                  paddingAngle={2}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="rgba(255,255,255,.06)" />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="min-w-0 overflow-hidden rounded-xl border border-white/5 bg-black/30 p-3 sm:p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-300">
            <BarChart3 className="h-4 w-4 shrink-0 text-cyan-400" /> Rank buckets (invoice amount, every 10 ranks)
          </div>
          <p className="mb-2 text-xs leading-snug text-slate-500">
            One bar per rank (by amount). Up to {RANK_PAGE_SIZE} bars per screen — use Previous / Next to move through the full list.
          </p>
          <div className="h-[260px] w-full max-w-full min-w-0 sm:h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barPageData} margin={{ top: 8, right: 8, left: 4, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} interval={0} />
                <YAxis width={48} tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(v) => formatCompact(Number(v))} />
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8 }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const row = payload[0].payload as { rank: number; value: number; entityName: string; label: string };
                    return (
                      <div className="rounded-lg border border-white/10 bg-slate-950/95 px-3 py-2 text-xs text-white shadow-lg">
                        <div className="font-medium text-cyan-300">Rank {row.rank}</div>
                        <div className="mt-1 max-w-[240px] break-words text-slate-200">{row.entityName}</div>
                        <div className="mt-1 font-mono text-cyan-200">{formatNum(row.value)}</div>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="value" fill="#22d3ee" radius={[4, 4, 0, 0]} maxBarSize={56} name="Amount" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {rankRows.length > RANK_PAGE_SIZE && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 border-white/20 bg-black/30 text-white hover:bg-white/10"
                disabled={safeRankPage <= 0}
                onClick={() => setRankPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </Button>
              <span className="text-center text-xs text-slate-400">
                Ranks {safeRankPage * RANK_PAGE_SIZE + 1}
                {"–"}
                {Math.min((safeRankPage + 1) * RANK_PAGE_SIZE, rankRows.length)} of {rankRows.length}
                <span className="text-slate-600"> · </span>
                Page {safeRankPage + 1} / {totalRankPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 border-white/20 bg-black/30 text-white hover:bg-white/10"
                disabled={safeRankPage >= totalRankPages - 1}
                onClick={() => setRankPage((p) => Math.min(totalRankPages - 1, p + 1))}
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/5 bg-black/20">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-slate-400">
              <th className="p-3 font-medium">#</th>
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium text-right">Composition</th>
              <th className="p-3 font-medium text-right">Current period</th>
              <th className="p-3 font-medium text-right">Prior period</th>
              <th className="p-3 font-medium text-right">Variance</th>
              <th className="p-3 font-medium">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map(([name, cyVal], idx) => {
              const pyVal = py?.[name] ?? 0;
              const rk = `${sectionId}-${idx + 1}`;
              const currentCompositionPct = total > EPS ? (cyVal / total) * 100 : 0;
              const priorCompositionPct = pyTotal > EPS ? (pyVal / pyTotal) * 100 : null;
              const variancePct = Math.abs(pyVal) > EPS ? ((cyVal - pyVal) / pyVal) * 100 : null;
              const varianceOverTen = variancePct != null && Math.abs(variancePct) > 10;
              const compositionShiftPct =
                priorCompositionPct != null && Math.abs(priorCompositionPct) > EPS
                  ? ((currentCompositionPct - priorCompositionPct) / priorCompositionPct) * 100
                  : null;
              const autoRemark =
                compositionShiftPct != null && Math.abs(compositionShiftPct) > 10
                  ? `Composition changed from ${Math.round(priorCompositionPct ?? 0)}% to ${Math.round(currentCompositionPct)}% (${Math.round(compositionShiftPct)}% vs prior).`
                  : "";
              const remarkValue = remarks[rk] ?? autoRemark;
              return (
                <tr key={rk} className="border-b border-white/5 text-slate-200">
                  <td className="p-3 text-slate-500">{idx + 1}</td>
                  <td className="max-w-[220px] truncate p-3" title={name}>
                    {name}
                  </td>
                  <td className="p-3 text-right font-mono text-slate-300">{Math.round(currentCompositionPct)}%</td>
                  <td className="p-3 text-right font-mono text-cyan-200">{formatNum(cyVal)}</td>
                  <td className="p-3 text-right font-mono text-slate-400">{formatNum(pyVal)}</td>
                  <td className="p-3 text-right font-mono">
                    {variancePct == null ? (
                      <span className="text-slate-500">—</span>
                    ) : (
                      <span className={varianceOverTen ? "text-red-400" : "text-slate-300"}>{Math.round(variancePct)}%</span>
                    )}
                  </td>
                  <td className="p-2">
                    <Textarea
                      value={remarkValue}
                      onChange={(e) => onRemark(rk, e.target.value)}
                      className="min-h-[52px] border-white/10 bg-black/40 text-xs text-white"
                      placeholder="Notes…"
                      disabled={saveRemarksDisabled}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {onSaveRemarks && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={onSaveRemarks}
              disabled={saveRemarksDisabled || isSavingRemarks}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {isSavingRemarks ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save remarks
            </Button>
            <span className="text-xs text-slate-500">
              Persists remarks for this pane to <code className="text-cyan-400/90">{USER_RESPONSES_FILE}</code>
              {" (ranks 1–10)."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RFORevenueAnalyticalDashboard() {
  const { isArchivalMode } = useArchivalMode();
  const [root, setRoot] = useState<RfoDashboardRoot>({});
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [userResponsesLoaded, setUserResponsesLoaded] = useState(false);
  const [savingSectionId, setSavingSectionId] = useState<string | null>(null);
  const [remarksSaveStatus, setRemarksSaveStatus] = useState<"idle" | "saved" | "error">("idle");
  const [remarksSaveError, setRemarksSaveError] = useState<string | null>(null);
  const [productRatePick, setProductRatePick] = useState<string>("");
  const [qtyProdPick, setQtyProdPick] = useState<string>("");

  const dates = useMemo(() => sortReportingDateKeys(Object.keys(root)), [root]);
  const prev = useMemo(() => (selected ? getPreviousReportingDate(dates, selected) : null), [dates, selected]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadErr(null);
      const { data, error } = await loadDashboardJson();
      if (cancelled) return;
      setRoot(data);
      const keys = sortReportingDateKeys(Object.keys(data));
      if (keys.length) setSelected((s) => (s && keys.includes(s) ? s : keys[keys.length - 1] ?? ""));
      if (error) setLoadErr(error);
      else if (!keys.length) {
        setLoadErr("No reporting periods found in Execution_RFO_Dashboard.json from SharePoint.");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await (window as unknown as { sharePointAPI?: { downloadClientJson?: (f: string) => Promise<unknown> } })
          .sharePointAPI?.downloadClientJson?.(USER_RESPONSES_FILE);
        const res = r as { success?: boolean; data?: unknown } | undefined;
        if (cancelled || !res?.success || !res.data || typeof res.data !== "object" || Array.isArray(res.data)) {
          return;
        }
        const data = res.data as { dashboard?: Record<string, unknown> };
        const dash = data.dashboard;
        if (!dash || typeof dash !== "object") return;
        setRemarks((prev) => {
          const n = { ...prev };
          for (const [sid, dk] of Object.entries(SECTION_TO_DASHBOARD_KEY)) {
            const block = dash[dk] as Record<string, unknown> | undefined;
            if (!block || typeof block !== "object") continue;
            for (let i = 1; i <= 10; i++) {
              const key = String(i);
              if (Object.prototype.hasOwnProperty.call(block, key)) {
                n[`${sid}-${i}`] = String(block[key] ?? "");
              }
            }
          }
          const rateMom = dash.product_rate_mom_remarks;
          if (rateMom && typeof rateMom === "object" && !Array.isArray(rateMom)) {
            for (const [storageKey, val] of Object.entries(rateMom as Record<string, string>)) {
              n[`${RATE_MOM_PREFIX}${storageKey}`] = String(val ?? "");
            }
          }
          const qtyTotal = dash.qty_total_remarks;
          if (qtyTotal && typeof qtyTotal === "object" && !Array.isArray(qtyTotal)) {
            for (const [storageKey, val] of Object.entries(qtyTotal as Record<string, string>)) {
              n[`${QTY_TOTAL_PREFIX}${storageKey}`] = String(val ?? "");
            }
          }
          const qtyProduct = dash.qty_product_remarks;
          if (qtyProduct && typeof qtyProduct === "object" && !Array.isArray(qtyProduct)) {
            for (const [storageKey, val] of Object.entries(qtyProduct as Record<string, string>)) {
              n[`${QTY_PRODUCT_PREFIX}${storageKey}`] = String(val ?? "");
            }
          }
          return n;
        });
      } catch {
        // optional file
      } finally {
        if (!cancelled) setUserResponsesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const payloadCy: ReportingPeriodPayload | undefined = selected ? root[selected] : undefined;
  const payloadPy: ReportingPeriodPayload | undefined = prev ? root[prev] : undefined;
  const dash: RevenueDashboardBlock | undefined = payloadCy?.["Revenue Analytical Dashboard"];
  const dashPy: RevenueDashboardBlock | undefined = payloadPy?.["Revenue Analytical Dashboard"];

  const top10Products = useMemo(() => {
    const m = dash?.["Product Type Analysis"];
    if (m && Object.keys(m).length > 0) {
      return entriesSortedByValueDesc(m)
        .slice(0, 10)
        .map(([k]) => k);
    }
    const rateMom = dash?.["Product Rate Analysis (MoM)"];
    if (rateMom && typeof rateMom === "object" && Object.keys(rateMom).length > 0) {
      return Object.keys(rateMom).slice(0, 10);
    }
    return [];
  }, [dash]);

  const visibleChartCount = useMemo(() => {
    if (!dash) return 0;
    const keys: (keyof RevenueDashboardBlock)[] = [
      "MoM Trend",
      "Customer Wise Analysis",
      "Incoterm Wise Analysis",
      "Export/Domestic Analysis",
      "Product Type Analysis",
      "Product Rate Analysis (MoM)",
      "MoM Quantity vs Value (Total)",
      "MoM Quantity vs Value (Product)",
      "Plant Location Analysis",
      "Region Analysis",
      "Transaction Type Analysis",
    ];
    return keys.filter((k) => dashboardHasSection(dash, k)).length;
  }, [dash]);

  useEffect(() => {
    if (!top10Products.length) {
      setProductRatePick("");
      setQtyProdPick("");
      return;
    }
    setProductRatePick((p) => (p && top10Products.includes(p) ? p : top10Products[0]!));
    setQtyProdPick((p) => (p && top10Products.includes(p) ? p : top10Products[0]!));
  }, [selected, top10Products]);

  const onRemark = useCallback((key: string, v: string) => {
    setRemarks((r) => ({ ...r, [key]: v }));
  }, []);

  const saveRemarksDisabled = isArchivalMode || !userResponsesLoaded;

  const handleSaveSectionRemarks = useCallback(
    async (sectionId: string) => {
      const dk = SECTION_TO_DASHBOARD_KEY[sectionId];
      if (!dk) return;
      setSavingSectionId(sectionId);
      setRemarksSaveStatus("idle");
      setRemarksSaveError(null);
      try {
        const fyYear = getFY_YEAR();
        if (!fyYear) {
          throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
        }
        const api = (window as unknown as {
          sharePointAPI?: {
            downloadClientJson?: (fileName: string) => Promise<{ success?: boolean; data?: unknown; error?: string }>;
            uploadFile?: (p: Record<string, unknown>) => Promise<{ success?: boolean; error?: string }>;
          };
        }).sharePointAPI;
        if (!api?.downloadClientJson || !api?.uploadFile) {
          throw new Error("SharePoint API is not available.");
        }

        const dl = await api.downloadClientJson(USER_RESPONSES_FILE);
        const existing =
          dl?.success && dl.data && typeof dl.data === "object" && !Array.isArray(dl.data)
            ? { ...(dl.data as Record<string, unknown>) }
            : {};

        const slot: Record<string, string> = {};
        for (let i = 1; i <= 10; i++) {
          slot[String(i)] = remarks[`${sectionId}-${i}`] ?? "";
        }

        const prevDash =
          existing.dashboard && typeof existing.dashboard === "object" && !Array.isArray(existing.dashboard)
            ? { ...(existing.dashboard as Record<string, unknown>) }
            : {};

        const mergedDashboard: Record<string, unknown> = {
          ...prevDash,
          [dk]: slot,
        };

        const merged: Record<string, unknown> = {
          ...existing,
          dashboard: mergedDashboard,
        };

        const jsonString = JSON.stringify(merged, null, 2);
        const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

        const up = await api.uploadFile({
          fileContent: base64Content,
          fileName: USER_RESPONSES_FILE,
          referenceValue: "",
          folderName: "juggernaut",
          fyYear,
          sectionList: [USER_RESPONSES_SECTION],
        });
        if (!up?.success) {
          throw new Error(up?.error || "Save failed.");
        }
        setRemarksSaveStatus("saved");
        setTimeout(() => setRemarksSaveStatus("idle"), 3000);
      } catch (e) {
        console.error("[RFO] Save dashboard remarks failed", e);
        setRemarksSaveStatus("error");
        setRemarksSaveError(e instanceof Error ? e.message : "Save failed.");
        setTimeout(() => {
          setRemarksSaveStatus("idle");
          setRemarksSaveError(null);
        }, 5000);
      } finally {
        setSavingSectionId(null);
      }
    },
    [remarks],
  );

  const handleSaveProductRateRemarks = useCallback(async () => {
    setSavingSectionId("rate");
    setRemarksSaveStatus("idle");
    setRemarksSaveError(null);
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }
      const api = (window as unknown as {
        sharePointAPI?: {
          downloadClientJson?: (fileName: string) => Promise<{ success?: boolean; data?: unknown; error?: string }>;
          uploadFile?: (p: Record<string, unknown>) => Promise<{ success?: boolean; error?: string }>;
        };
      }).sharePointAPI;
      if (!api?.downloadClientJson || !api?.uploadFile) {
        throw new Error("SharePoint API is not available.");
      }

      const dl = await api.downloadClientJson(USER_RESPONSES_FILE);
      const existing =
        dl?.success && dl.data && typeof dl.data === "object" && !Array.isArray(dl.data)
          ? { ...(dl.data as Record<string, unknown>) }
          : {};

      const prevDash =
        existing.dashboard && typeof existing.dashboard === "object" && !Array.isArray(existing.dashboard)
          ? { ...(existing.dashboard as Record<string, unknown>) }
          : {};

      const rateMomMap: Record<string, string> = {};
      for (const [k, v] of Object.entries(remarks)) {
        if (k.startsWith(RATE_MOM_PREFIX)) {
          rateMomMap[k.slice(RATE_MOM_PREFIX.length)] = v;
        }
      }

      const merged: Record<string, unknown> = {
        ...existing,
        dashboard: {
          ...prevDash,
          product_rate_mom_remarks: rateMomMap,
        },
      };

      const jsonString = JSON.stringify(merged, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const up = await api.uploadFile({
        fileContent: base64Content,
        fileName: USER_RESPONSES_FILE,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: [USER_RESPONSES_SECTION],
      });
      if (!up?.success) {
        throw new Error(up?.error || "Save failed.");
      }
      setRemarksSaveStatus("saved");
      setTimeout(() => setRemarksSaveStatus("idle"), 3000);
    } catch (e) {
      console.error("[RFO] Save product rate remarks failed", e);
      setRemarksSaveStatus("error");
      setRemarksSaveError(e instanceof Error ? e.message : "Save failed.");
      setTimeout(() => {
        setRemarksSaveStatus("idle");
        setRemarksSaveError(null);
      }, 5000);
    } finally {
      setSavingSectionId(null);
    }
  }, [remarks]);

  const handleSaveQtyRemarks = useCallback(async () => {
    setSavingSectionId("qty");
    setRemarksSaveStatus("idle");
    setRemarksSaveError(null);
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }
      const api = (window as unknown as {
        sharePointAPI?: {
          downloadClientJson?: (fileName: string) => Promise<{ success?: boolean; data?: unknown; error?: string }>;
          uploadFile?: (p: Record<string, unknown>) => Promise<{ success?: boolean; error?: string }>;
        };
      }).sharePointAPI;
      if (!api?.downloadClientJson || !api?.uploadFile) {
        throw new Error("SharePoint API is not available.");
      }

      const dl = await api.downloadClientJson(USER_RESPONSES_FILE);
      const existing =
        dl?.success && dl.data && typeof dl.data === "object" && !Array.isArray(dl.data)
          ? { ...(dl.data as Record<string, unknown>) }
          : {};

      const prevDash =
        existing.dashboard && typeof existing.dashboard === "object" && !Array.isArray(existing.dashboard)
          ? { ...(existing.dashboard as Record<string, unknown>) }
          : {};

      const qtyTotalMap: Record<string, string> = {};
      const qtyProductMap: Record<string, string> = {};
      for (const [k, v] of Object.entries(remarks)) {
        if (k.startsWith(QTY_TOTAL_PREFIX)) {
          qtyTotalMap[k.slice(QTY_TOTAL_PREFIX.length)] = v;
        } else if (k.startsWith(QTY_PRODUCT_PREFIX)) {
          qtyProductMap[k.slice(QTY_PRODUCT_PREFIX.length)] = v;
        }
      }

      const merged: Record<string, unknown> = {
        ...existing,
        dashboard: {
          ...prevDash,
          qty_total_remarks: qtyTotalMap,
          qty_product_remarks: qtyProductMap,
        },
      };

      const jsonString = JSON.stringify(merged, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const up = await api.uploadFile({
        fileContent: base64Content,
        fileName: USER_RESPONSES_FILE,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: [USER_RESPONSES_SECTION],
      });
      if (!up?.success) {
        throw new Error(up?.error || "Save failed.");
      }
      setRemarksSaveStatus("saved");
      setTimeout(() => setRemarksSaveStatus("idle"), 3000);
    } catch (e) {
      console.error("[RFO] Save qty remarks failed", e);
      setRemarksSaveStatus("error");
      setRemarksSaveError(e instanceof Error ? e.message : "Save failed.");
      setTimeout(() => {
        setRemarksSaveStatus("idle");
        setRemarksSaveError(null);
      }, 5000);
    } finally {
      setSavingSectionId(null);
    }
  }, [remarks]);

  const momTrendData = useMemo(() => {
    const m = dash?.["MoM Trend"];
    if (!m) return [];
    return sortMonthKeys(Object.keys(m)).map((k) => ({ month: k, amount: m[k] }));
  }, [dash]);

  const productRateSeries = useMemo(() => {
    if (!dash || !productRatePick) return [];
    const raw = dash["Product Rate Analysis (MoM)"]?.[productRatePick] ?? {};
    return sortMonthKeys(Object.keys(raw)).map((month) => {
      const rate = raw[month] ?? 0;
      return { month, rate };
    });
  }, [dash, productRatePick]);

  const productRateTableRows = useMemo(() => {
    if (!dash || !productRatePick) return [];
    const data = dash["Product Rate Analysis (MoM)"]?.[productRatePick] ?? {};
    return buildProductRateMomTableRows({ [productRatePick]: data });
  }, [dash, productRatePick]);

  const momTotalChart = useMemo(() => {
    const m = dash?.["MoM Quantity vs Value (Total)"];
    if (!m) return [];
    return sortMonthKeys(Object.keys(m)).map((month) => {
      const row = m[month];
      const qty = row?.billing_quantity ?? 0;
      const amt = row?.invoice_amount ?? 0;
      return { month, qty, amt };
    });
  }, [dash]);

  const momTotalDivergence = useMemo(() => {
    if (!momTotalChart.length) return [];
    const maxQ = Math.max(...momTotalChart.map((d) => Math.abs(d.qty)), EPS);
    const maxA = Math.max(...momTotalChart.map((d) => Math.abs(d.amt)), EPS);
    return momTotalChart.map((d) => {
      const nq = d.qty / maxQ;
      const na = d.amt / maxA;
      const div = Math.abs(nq - na);
      return { ...d, highlight: div > 0.1 };
    });
  }, [momTotalChart]);

  const momProductChart = useMemo(() => {
    if (!dash || !qtyProdPick) return [];
    const m = dash["MoM Quantity vs Value (Product)"]?.[qtyProdPick] ?? {};
    return sortMonthKeys(Object.keys(m)).map((month) => {
      const row = m[month];
      const qty = row?.billing_quantity ?? 0;
      const amt = row?.invoice_amount ?? 0;
      return { month, qty, amt };
    });
  }, [dash, qtyProdPick]);

  const momProductDivergence = useMemo(() => {
    if (!momProductChart.length) return [];
    const maxQ = Math.max(...momProductChart.map((d) => Math.abs(d.qty)), EPS);
    const maxA = Math.max(...momProductChart.map((d) => Math.abs(d.amt)), EPS);
    return momProductChart.map((d) => {
      const nq = d.qty / maxQ;
      const na = d.amt / maxA;
      const div = Math.abs(nq - na);
      return { ...d, highlight: div > 0.1 };
    });
  }, [momProductChart]);

  if (loading) {
    return (
      <Card className="border-white/10 bg-black/40">
        <CardContent className="py-10 text-center text-slate-400">Loading Revenue Analytical Dashboard…</CardContent>
      </Card>
    );
  }

  if (loadErr || !dates.length) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="py-8 text-amber-100/90">{loadErr ?? "No data."}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-2xl border border-cyan-500/20 bg-gradient-to-r from-cyan-950/40 to-slate-950/60 p-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-500/20 text-cyan-300">
            <LayoutDashboard className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-white">Revenue Analytical Dashboard</h2>
            <p className="text-sm text-slate-400">
              Data loaded from SharePoint (<code className="text-cyan-400/90">juggernaut/Execution_RFO_Dashboard.json</code> for the
              current engagement)
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">Reporting date</Label>
            <Select data-allow-archival-edit value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-[200px] border-white/10 bg-black/50 text-white">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-slate-950 text-white">
                {dates.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {prev && (
            <p className="text-xs text-slate-500">
              Prior period for comparison: <span className="text-slate-300">{prev}</span>
            </p>
          )}
        </div>
        {remarksSaveStatus === "saved" && (
          <p className="text-sm text-emerald-400/90">Remarks saved to {USER_RESPONSES_FILE}.</p>
        )}
        {remarksSaveStatus === "error" && remarksSaveError && (
          <p className="text-sm text-rose-300/90">{remarksSaveError}</p>
        )}
      </div>

      {dates.length === 1 && (
        <Card className="border-slate-600/40 bg-slate-900/40">
          <CardContent className="py-4 text-sm text-slate-400">
            Only one reporting period is present. Add another run in <code className="text-cyan-400">Execution_RFO_Dashboard.json</code> to
            compare against a prior reporting date in the customer tables.
          </CardContent>
        </Card>
      )}

      {dates.length > 1 && !prev && (
        <Card className="border-slate-600/40 bg-slate-900/40">
          <CardContent className="py-4 text-sm text-slate-400">
            You selected the earliest reporting date in the file — there is no prior period for the “Prior period” column in customer
            analysis.
          </CardContent>
        </Card>
      )}

      {visibleChartCount === 0 && selected && (
        <Card className="border-white/10 bg-black/40">
          <CardContent className="py-8 text-center text-slate-400">
            No dashboard charts for this reporting date. Only sections present under{" "}
            <code className="text-cyan-400/90">Revenue Analytical Dashboard</code> in the JSON for that date are shown. Map optional
            columns (for example invoice amount) and run IPE again if you need additional charts.
          </CardContent>
        </Card>
      )}

      {visibleChartCount > 0 && (
        <div className="relative -mx-1 sm:-mx-0">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-slate-950 to-transparent sm:w-8" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-slate-950 to-transparent sm:w-8" />
          <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-4 scroll-py-1 pb-4 pt-1 px-3 sm:gap-5 sm:scroll-px-6 sm:px-4 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/10">
            {dashboardHasSection(dash, "MoM Trend") && (
          <ScrollSection
            id="pane-mom"
            title="MoM Trend"
            subtitle="Invoice amount by calendar month (from mapped register)"
            icon={LineChartIcon}
          >
            <div className="h-[340px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={momTrendData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10 }} angle={-35} textAnchor="end" height={70} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => formatCompact(Number(v))} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8 }}
                    formatter={(v: number) => formatNum(v)}
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#22d3ee"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Invoice amount"
                  >
                    <LabelList
                      dataKey="amount"
                      position="top"
                      formatter={(v) => formatCompact(Number(v))}
                      className="fill-slate-400"
                      fontSize={10}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Customer Wise Analysis") && (
          <ScrollSection id="pane-cust" title="Customer wise analysis" icon={PieIcon}>
            <CategoryWisePane
              cy={dash?.["Customer Wise Analysis"]}
              py={dashPy?.["Customer Wise Analysis"]}
              sectionId="cust"
              remarks={remarks}
              onRemark={onRemark}
              onSaveRemarks={() => handleSaveSectionRemarks("cust")}
              isSavingRemarks={savingSectionId === "cust"}
              saveRemarksDisabled={saveRemarksDisabled}
            />
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Incoterm Wise Analysis") && (
          <ScrollSection id="pane-inco" title="Incoterm wise analysis" icon={PieIcon}>
            <CategoryWisePane
              cy={dash?.["Incoterm Wise Analysis"]}
              py={dashPy?.["Incoterm Wise Analysis"]}
              sectionId="inco"
              remarks={remarks}
              onRemark={onRemark}
              onSaveRemarks={() => handleSaveSectionRemarks("inco")}
              isSavingRemarks={savingSectionId === "inco"}
              saveRemarksDisabled={saveRemarksDisabled}
            />
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Export/Domestic Analysis") && (
          <ScrollSection id="pane-exp" title="Export / Domestic analysis" icon={PieIcon}>
            <CategoryWisePane
              cy={dash?.["Export/Domestic Analysis"]}
              py={dashPy?.["Export/Domestic Analysis"]}
              sectionId="exp"
              remarks={remarks}
              onRemark={onRemark}
              onSaveRemarks={() => handleSaveSectionRemarks("exp")}
              isSavingRemarks={savingSectionId === "exp"}
              saveRemarksDisabled={saveRemarksDisabled}
            />
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Product Type Analysis") && (
          <ScrollSection id="pane-prod" title="Product type analysis" icon={PieIcon}>
            <CategoryWisePane
              cy={dash?.["Product Type Analysis"]}
              py={dashPy?.["Product Type Analysis"]}
              sectionId="prod"
              remarks={remarks}
              onRemark={onRemark}
              onSaveRemarks={() => handleSaveSectionRemarks("prod")}
              isSavingRemarks={savingSectionId === "prod"}
              saveRemarksDisabled={saveRemarksDisabled}
            />
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Product Rate Analysis (MoM)") && (
          <ScrollSection
            id="pane-rate"
            title="Product rate analysis (MoM)"
            subtitle="Unit rate = invoice amount ÷ billing quantity · highlights when month-on-month &gt;10%"
            icon={LineChartIcon}
          >
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Label className="text-slate-400">Product (top 10 by revenue)</Label>
              <Select data-allow-archival-edit value={productRatePick} onValueChange={setProductRatePick}>
                <SelectTrigger className="w-[min(100%,320px)] border-white/10 bg-black/40 text-white">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-slate-950 text-white">
                  {top10Products.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p.length > 48 ? `${p.slice(0, 46)}…` : p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={productRateSeries} margin={{ top: 16, right: 12, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => formatNum(Number(v))} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8 }}
                    formatter={(v: number) => formatNum(v)}
                  />
                  <Line type="monotone" dataKey="rate" stroke="#f472b6" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto rounded-xl border border-white/5 bg-black/20">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-slate-400">
                    <th className="p-3 font-medium">Product</th>
                    <th className="p-3 font-medium">Month</th>
                    <th className="p-3 text-right font-medium">Rate</th>
                    <th className="p-3 text-right font-medium">Variance vs prev month</th>
                    <th className="p-3 font-medium">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {productRateTableRows.map((row) => {
                    const rk = rateMomRemarkKey(row.product, row.month);
                    return (
                      <tr key={rk} className="border-b border-white/5 text-slate-200">
                        <td className="max-w-[220px] p-3 align-top break-words" title={row.product}>
                          {row.product}
                        </td>
                        <td className="whitespace-nowrap p-3 font-mono text-xs align-top text-slate-300">{row.month}</td>
                        <td className="p-3 text-right font-mono align-top text-cyan-200">{formatNum(row.rate)}</td>
                        <td className="p-3 text-right font-mono align-top">
                          {row.variancePct == null ? (
                            <span className="text-slate-500">—</span>
                          ) : (
                            <span className={row.overTenPct ? "font-semibold text-red-400" : "text-slate-300"}>
                              {Math.round(row.variancePct)}%
                            </span>
                          )}
                        </td>
                        <td className="p-2 align-top">
                          <Textarea
                            value={remarks[rk] ?? ""}
                            onChange={(e) => onRemark(rk, e.target.value)}
                            className="min-h-[44px] border-white/10 bg-black/40 text-xs text-white"
                            placeholder="Notes…"
                            disabled={saveRemarksDisabled}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/5 px-2 pb-2 pt-3">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveProductRateRemarks}
                  disabled={saveRemarksDisabled || savingSectionId === "rate"}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {savingSectionId === "rate" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save remarks
                </Button>
                <span className="text-xs text-slate-500">
                  Persists product rate remarks to <code className="text-cyan-400/90">{USER_RESPONSES_FILE}</code>.
                </span>
              </div>
            </div>
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "MoM Quantity vs Value (Total)") && (
          <ScrollSection
            id="pane-qty-t"
            title="MoM quantity vs value (total)"
            subtitle="Line = billing quantity · bars = invoice amount (dual axis). Months with normalized shape divergence &gt;10% are highlighted in the table below."
            icon={BarChart3}
          >
            <div className="h-[360px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={momTotalDivergence} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: "#67e8f9", fontSize: 11 }}
                    tickFormatter={(v) => formatCompact(Number(v))}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: "#c4b5fd", fontSize: 11 }}
                    tickFormatter={(v) => formatCompact(Number(v))}
                  />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8 }}
                    formatter={(v: number, name: string) => {
                      const label =
                        name === "Invoice amount" || name === "amt"
                          ? "Invoice amount"
                          : name === "Billing quantity" || name === "qty"
                            ? "Billing quantity"
                            : name;
                      return [formatNum(v), label];
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="right" dataKey="amt" fill="#7c3aed" name="Invoice amount" radius={[4, 4, 0, 0]} opacity={0.85} />
                  <Line yAxisId="left" type="monotone" dataKey="qty" stroke="#22d3ee" strokeWidth={2} dot name="Billing quantity" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto text-xs">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-slate-500">
                    <th className="p-2">Month</th>
                    <th className="p-2">Divergence flag</th>
                    <th className="p-2">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {momTotalDivergence.map((row) => (
                    <tr key={row.month} className={row.highlight ? "bg-amber-500/10 text-amber-100" : "text-slate-300"}>
                      <td className="p-2 font-mono">{row.month}</td>
                      <td className="p-2">{row.highlight ? "Normalized qty vs amount shapes differ by >10%" : "—"}</td>
                      <td className="p-2">
                        <Textarea
                          value={remarks[qtyTotalRemarkKey(row.month)] ?? ""}
                          onChange={(e) => onRemark(qtyTotalRemarkKey(row.month), e.target.value)}
                          className="min-h-[44px] border-white/10 bg-black/40 text-xs text-white"
                          placeholder="Notes…"
                          disabled={saveRemarksDisabled}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/5 px-2 pb-2 pt-3">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveQtyRemarks}
                  disabled={saveRemarksDisabled || savingSectionId === "qty"}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {savingSectionId === "qty" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save remarks
                </Button>
                <span className="text-xs text-slate-500">
                  Persists MoM quantity/value remarks to <code className="text-cyan-400/90">{USER_RESPONSES_FILE}</code>.
                </span>
              </div>
            </div>
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "MoM Quantity vs Value (Product)") && (
          <ScrollSection
            id="pane-qty-p"
            title="MoM quantity vs value (product)"
            subtitle="Same as total, for one of the top 10 products"
            icon={BarChart3}
          >
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Label className="text-slate-400">Product</Label>
              <Select data-allow-archival-edit value={qtyProdPick} onValueChange={setQtyProdPick}>
                <SelectTrigger className="w-[min(100%,320px)] border-white/10 bg-black/40 text-white">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-slate-950 text-white">
                  {top10Products.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p.length > 48 ? `${p.slice(0, 46)}…` : p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="h-[360px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={momProductDivergence} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fill: "#67e8f9", fontSize: 11 }} tickFormatter={(v) => formatCompact(Number(v))} />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: "#c4b5fd", fontSize: 11 }}
                    tickFormatter={(v) => formatCompact(Number(v))}
                  />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.1)", borderRadius: 8 }}
                    formatter={(v: number, name: string) => {
                      const label =
                        name === "Invoice amount" || name === "amt"
                          ? "Invoice amount"
                          : name === "Billing quantity" || name === "qty"
                            ? "Billing quantity"
                            : name;
                      return [formatNum(v), label];
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="right" dataKey="amt" fill="#a78bfa" name="Invoice amount" radius={[4, 4, 0, 0]} opacity={0.88} />
                  <Line yAxisId="left" type="monotone" dataKey="qty" stroke="#2dd4bf" strokeWidth={2} dot name="Billing quantity" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto text-xs">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-slate-500">
                    <th className="p-2">Month</th>
                    <th className="p-2">Divergence flag</th>
                    <th className="p-2">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {momProductDivergence.map((row) => (
                    <tr key={row.month} className={row.highlight ? "bg-amber-500/10 text-amber-100" : "text-slate-300"}>
                      <td className="p-2 font-mono">{row.month}</td>
                      <td className="p-2">{row.highlight ? "Normalized qty vs amount shapes differ by >10%" : "—"}</td>
                      <td className="p-2">
                        <Textarea
                          value={remarks[qtyProductRemarkKey(qtyProdPick, row.month)] ?? ""}
                          onChange={(e) => onRemark(qtyProductRemarkKey(qtyProdPick, row.month), e.target.value)}
                          className="min-h-[44px] border-white/10 bg-black/40 text-xs text-white"
                          placeholder="Notes…"
                          disabled={saveRemarksDisabled}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/5 px-2 pb-2 pt-3">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveQtyRemarks}
                  disabled={saveRemarksDisabled || savingSectionId === "qty"}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {savingSectionId === "qty" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save remarks
                </Button>
                <span className="text-xs text-slate-500">
                  Persists MoM quantity/value remarks to <code className="text-cyan-400/90">{USER_RESPONSES_FILE}</code>.
                </span>
              </div>
            </div>
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Plant Location Analysis") && (
          <ScrollSection id="pane-plant" title="Plant location analysis" icon={PieIcon}>
            <CategoryWisePane
              cy={dash?.["Plant Location Analysis"]}
              py={dashPy?.["Plant Location Analysis"]}
              sectionId="plant"
              remarks={remarks}
              onRemark={onRemark}
              onSaveRemarks={() => handleSaveSectionRemarks("plant")}
              isSavingRemarks={savingSectionId === "plant"}
              saveRemarksDisabled={saveRemarksDisabled}
            />
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Region Analysis") && (
          <ScrollSection id="pane-region" title="Region analysis" icon={PieIcon}>
            <CategoryWisePane
              cy={dash?.["Region Analysis"]}
              py={dashPy?.["Region Analysis"]}
              sectionId="region"
              remarks={remarks}
              onRemark={onRemark}
              onSaveRemarks={() => handleSaveSectionRemarks("region")}
              isSavingRemarks={savingSectionId === "region"}
              saveRemarksDisabled={saveRemarksDisabled}
            />
          </ScrollSection>
            )}

            {dashboardHasSection(dash, "Transaction Type Analysis") && (
          <ScrollSection id="pane-tx" title="Transaction type analysis" icon={Table2}>
            <CategoryWisePane
              cy={dash?.["Transaction Type Analysis"]}
              py={dashPy?.["Transaction Type Analysis"]}
              sectionId="tx"
              remarks={remarks}
              onRemark={onRemark}
              onSaveRemarks={() => handleSaveSectionRemarks("tx")}
              isSavingRemarks={savingSectionId === "tx"}
              saveRemarksDisabled={saveRemarksDisabled}
            />
          </ScrollSection>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
